import { ErrorSidePanel } from '@/components/errors';
import { ModuleName, Screen, type SkinPageConfig, templates } from '@nosinovacao/nosid-mfe-common';
import { createFileRoute } from '@tanstack/react-router';
import { useGetAccountActivity } from './-hooks/get-account-activity.hook';
import { useGetApplicationsNames } from './-hooks/get-application-names.hook';
import { type ActivityLogsProps, type FilterItem } from './-model/props';
import { type AccountActivityQuery, accountActivityQuerySchema } from './-model/queryFilters';
import { createDeviceGroupMap } from './-model/utils';
import { ActivityLogsLight } from './-skins';
import { ALL_FILTER } from './filters/-model/props';

function getFilterItems(
  query: AccountActivityQuery,
  applications: Record<string, string>,
): Array<FilterItem> {
  const filterItems: FilterItem[] = [];

  if (query.orders.length) {
    filterItems.push(
      ...query.orders.map<FilterItem>((a) => ({
        id: a,
        query: { ...query, orders: query.orders.filter((act) => act !== a) },
        label: `AccountActivity.Filters.Order.Values.${a}`,
      })),
    );
  }
  if (query.activities.length) {
    filterItems.push(
      ...query.activities.map<FilterItem>((a) => ({
        id: a,
        query: { ...query, activities: query.activities.filter((act) => act !== a) },
        label: `AccountActivity.Type.${a}`,
      })),
    );
  }

  if (query.devices.length) {
    const deviceGroupMap = createDeviceGroupMap(query.devices);
    for (const [normalized, originals] of deviceGroupMap) {
      filterItems.push({
        id: normalized,
        query: { ...query, devices: query.devices.filter((d) => !originals.includes(d)) },
        label: normalized,
      });
    }
  }

  if (query.applications.length) {
    filterItems.push(
      ...query.applications.map<FilterItem>((a) => ({
        id: a,
        query: { ...query, applications: query.applications.filter((act) => act !== a) },
        label: applications[a] ?? a,
      })),
    );
  }

  return filterItems.filter((f) => f.id !== ALL_FILTER);
}

export const Route = createFileRoute('/(security)/security/_sheet/account-activity/')({
  component: RouteComponent,
  errorComponent: ErrorSidePanel,
  staticData: {
    module: ModuleName.Security,
    screen: Screen.AccountActivity,
  },
  validateSearch: accountActivityQuerySchema,
});

function RouteComponent() {
  const query = Route.useSearch();
  const { data, isLoading, isFetchingNextPage, fetchNextPage, hasNextPage } =
    useGetAccountActivity(query);
  const { data: applicationNames, isLoading: isLoadingApplications } = useGetApplicationsNames();
  const logs = data?.pages.flatMap((p) => p.Items) ?? [];

  const filterItems = getFilterItems(query, applicationNames ?? {});

  const props: ActivityLogsProps = {
    logs,
    loading: isLoading || isFetchingNextPage || isLoadingApplications,
    loadMore: hasNextPage
      ? () => {
          void fetchNextPage();
        }
      : () => {},
    applicationNames: applicationNames,
    currentQuery: query,
    filterItems: filterItems,
  };
  return templates.getSkinSelector(skinConfig(props));
}
function skinConfig(props: ActivityLogsProps): SkinPageConfig {
  return {
    skins: [],
    defaultValue: <ActivityLogsLight {...props} />,
  };
}
